export type DocumentId = string;
export type BlockId = string;
export type StorageRevision = string;

export const DOCUMENT_FORMAT = "z-editor";
export const CURRENT_SCHEMA_VERSION = 2;

/** Identifies which editing engine last wrote the content. Informational; never drives migration. */
export interface EngineInfo {
  name: "lexical";
  version: string;
}

/** Schema 1: Lexical JSON inside our own versioned envelope. Blocks have no persistent identity. */
export interface EditorDocumentV1 {
  format: typeof DOCUMENT_FORMAT;
  schemaVersion: 1;
  documentId: DocumentId;
  engine: EngineInfo;
  content: SerializedEditorState;
}

/**
 * Schema 2: every block and list item carries a persistent ID in its NodeState
 * (`"$": { "blockId": "…" }`), unique within the document.
 */
export interface EditorDocumentV2 extends Omit<EditorDocumentV1, "schemaVersion"> {
  schemaVersion: 2;
}

/** The canonical persisted document, at the current schema version. */
export type EditorDocument = EditorDocumentV2;

export interface StoredDocument {
  document: EditorDocument;
  storageRevision: StorageRevision;
}

export interface ExportWarning {
  code: string;
  message: string;
  blockId?: BlockId;
}

export interface ExportResult {
  text: string;
  warnings: ExportWarning[];
}

export type EditorPreset = "composer" | "description" | "document";

/** What a block can be turned into. */
export type BlockType =
  | "paragraph"
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "quote"
  | "code"
  | "bullet"
  | "number"
  | "check";

export type JsonValue =
  | null
  | boolean
  | number
  | string
  | JsonValue[]
  | { [key: string]: JsonValue };

/*
 * Lexical's serialized shapes, written out here rather than imported: the document contract is
 * read by code that never loads Lexical (the renderer, persistence, a server that validates), and a
 * type import would still put `lexical` in their declarations. `test/unit/core/serialized-types.test.ts`
 * holds these assignable to and from Lexical's own, in both directions.
 */

/** What every serialized node has. `$` is Lexical's NodeState, where a block's ID is kept. */
export type SerializedLexicalNode = {
  type: string;
  version: number;
  $?: Record<string, unknown>;
};

export type SerializedElementNode = SerializedLexicalNode & {
  children: SerializedLexicalNode[];
  direction: "ltr" | "rtl" | null;
  format: "left" | "start" | "center" | "right" | "end" | "justify" | "";
  indent: number;
  textFormat?: number;
  textStyle?: string;
};

export interface SerializedEditorState {
  root: SerializedElementNode;
}

/** A serialized node as the validator and static renderer see it: a type tag plus unknown fields. */
export type SerializedNodeLike = SerializedLexicalNode & {
  children?: SerializedNodeLike[];
  [key: string]: unknown;
};
